import { observable, action, runInAction, makeObservable } from 'mobx';
import moment from 'moment';

import { StatNumber, StatDeviceSum, StatDateNumbers, StatUnit, statUnit, DailyStats } from '../models/stat.model';
import { IReleaseMarker } from '../models/release.model';
import { RangeMode } from '../models/search.model';
import Popup from '../components/common/popup.component';
import statsConnection from '../services/connection/stats.connection';
import releaseConnection from '../services/connection/release.connection';
import memberStore from './member.store';
import commonStore from './common.store';
import loglyticsStore from './loglytics.store';
import { rangeModeToFromTime, rangeModeToInterval } from '../components/common/time_range_buttons.component';
import { getLogger } from '../services/logger.service';

const log = getLogger('DashboardStore');

export class DashboardStore {
  inProgress = false;
  query: string = '';
  
  appVersions: string[] = [];
  version: string = '';
  range: RangeMode = RangeMode.HOUR;

  average: StatNumber = {exception: 0, error: 0, warning: 0};
  deviceSums: StatDeviceSum[] = [];
  logSums: StatDateNumbers[] = [];
  affectedDevices: StatDateNumbers[] = [];
  dailyStats?: DailyStats = undefined;
  markers: IReleaseMarker[] = [];

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      query: observable,
      appVersions: observable,
      version: observable,
      range: observable,
      average: observable,
      deviceSums: observable,
      logSums: observable,
      affectedDevices: observable,
      dailyStats: observable,
      markers: observable,
      getStats: action,
      setVersion: action,
      setRange: action
    });

    this.fillMissingValues = this.fillMissingValues.bind(this);
  }
  // @computed getAf
  async getStats() {
    this.inProgress = true;
    commonStore.setLoading(true);
    try {
      const data: { appVersion?: string; startDate: string; timeInterval: string; statUnit: StatUnit } = {
        startDate: rangeModeToFromTime(this.range).toISOString(),
        timeInterval: rangeModeToInterval(this.range, memberStore.app?.platform),
        statUnit: statUnit(memberStore.app?.platform),
      };
      if (this.version.length > 0) data.appVersion = this.version;
      // dailyStats serves only completed rollup days, so its window ends
      // yesterday and never covers less than 7 days (30 for ALL).
      const dailyStatsData: { appVersion?: string; startDate: string; endDate: string; statUnit: StatUnit } = {
        startDate: moment().subtract(this.range === RangeMode.ALL ? 30 : 7, 'd').startOf('day').toISOString(),
        endDate: moment().subtract(1, 'd').toISOString(),
        statUnit: data.statUnit,
      };
      if (data.appVersion) dailyStatsData.appVersion = data.appVersion;
      const [appVersions, average, deviceSums, logSums, affectedDevices, dailyStats, markers] = await Promise.all([
        statsConnection.getAppVersions(data),
        statsConnection.getAverages(data),
        statsConnection.getDeviceSums(data),
        statsConnection.getLogSums(data),
        statsConnection.getAffectedDevices(data),
        statsConnection.getDailyStats(dailyStatsData),
        // plan-gated endpoint — a markers failure must not take down the whole Bridge
        releaseConnection.getMarkers({fromTime: data.startDate, toTime: moment().toISOString()})
          .catch(error => { log.e('error getting release markers', error); return []; }),
      ]);
      runInAction(() => {
        this.appVersions = appVersions;
        this.average = average;
        this.deviceSums = this.fillMissingValues(deviceSums);
        this.logSums = this.fillMissingValues(logSums) as StatDateNumbers[];
        this.affectedDevices = this.fillMissingValues(affectedDevices) as StatDateNumbers[];
        this.dailyStats = dailyStats;
        this.markers = markers;
      });
    } catch (error) {
      log.e('got the following error', error);
      Popup.createError({msg: 'Had a problem loading the dashboard info, please try later again'}); 
    } finally {
      commonStore.setLoading(false);
      runInAction(() => this.inProgress = false);
    }
  }

  // tslint:disable-next-line:no-shadowed-variable
  setVersion(version: string) {
    this.version = version;
    this.getStats();
  }

  setRange(range: RangeMode) {
    this.range = range;
    this.getStats();
    loglyticsStore.setRange(range);
  }


  private fillMissingValues(dates: {time: Date}[]) {
  //   const currentDate = moment();
  //   let date: moment.Moment;
  //   let unit: moment.DurationInputArg2;
  //   if (this.dateUnit === DateUnit.MONTH) {
  //     date = moment().subtract(1, 'month').startOf('day');
  //     unit = 'day';
  //   }
  //   else {
  //     date = moment().subtract(1, 'day').startOf('hour');
  //     unit = 'hour';
  //   }

  //   let newDates: {time: Date}[] = [];
  //   for (let i = 0; date.isBefore(currentDate) ; date = date.add(1, unit)) {
  //     if (i < dates.length && date.isSame(dates[i].time, unit)) newDates.push(dates[i++]);
  //     else {
  //       newDates.push({
  //         time: date.toDate()
  //       });
  //     }
  //   }
  //   return newDates;
  // }
    return dates;
  }
}

export default new DashboardStore();
